import React, { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { ErrorCode, PurchasesError } from '@revenuecat/purchases-js'
import ModalWrapper from '../../ModalWrapper'
import SuccessModal from '../../ModalComponents/SuccessModal'
import ErrorModal from '../../ModalComponents/ErrorModal'
import BDCoinIcon from '../../../Assets/icons/coin.png'
import { useRevenueCat } from '../../../hooks/useRevenueCat'

export default function BuyCoinsPaymentModal({ offer, onClose }) {
    const queryClient = useQueryClient()
    const { purchaseCoinPackage } = useRevenueCat()
    const [step, setStep] = useState('confirm') // confirm | processing | success | error
    const [errorMessage, setErrorMessage] = useState('')

    const handlePurchase = async () => {
        try {
            setStep('processing')
            await purchaseCoinPackage(offer)
            queryClient.invalidateQueries({ queryKey: ['wallet', 'details'] })
            queryClient.invalidateQueries({ queryKey: ['wallet', 'history'] })
            setStep('success')
        } catch (error) {
            if (error instanceof PurchasesError && error.errorCode === ErrorCode.UserCancelledError) {
                setStep('confirm')
                return
            }
            setErrorMessage(error?.message || 'Something went wrong while processing your purchase.')
            setStep('error')
        }
    }

    if (step === 'success') {
        return (
            <SuccessModal
                message={`${offer.coins?.toLocaleString()} BD Coins added to your wallet!`}
                onClose={onClose}
            />
        )
    }

    if (step === 'error') {
        return (
            <ErrorModal
                message={errorMessage}
                onClose={onClose}
                onRetry={() => setStep('confirm')}
            />
        )
    }

    // RevenueCat's own checkout renders as a full-page overlay once
    // purchaseCoinPackage() is called. ModalWrapper sits at the maximum
    // z-index so it stays on top of everything else on the page - which
    // would otherwise hide RC's checkout underneath it and leave the user
    // with nothing clickable. Drop to a low, non-blocking placeholder here
    // instead so RC's overlay is the only interactive surface while a
    // purchase is in flight.
    if (step === 'processing') {
        return (
            <div className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
                <div className="flex flex-col items-center gap-3 text-white">
                    <div className="w-10 h-10 border-4 border-white/20 border-t-[#c9a227] rounded-full animate-spin" />
                    <p className="text-sm text-gray-300">Preparing secure checkout...</p>
                </div>
            </div>
        )
    }

    return (
        <ModalWrapper onClose={onClose} title="Confirm Purchase" zIndex='50'>
            <div className="p-4">
                <div className="flex items-center justify-between bg-[#4a4a4a] rounded-xl p-4 mb-6">
                    <div className="flex items-center gap-2">
                        <img src={BDCoinIcon} alt="bd-coin-icon" className="w-5 h-5" />
                        <span className="font-bold text-white">{offer.coins?.toLocaleString()} BD Coins</span>
                    </div>
                    <span className="text-gray-300 font-medium">{offer.price}</span>
                </div>

                <p className="text-sm text-gray-400 mb-6">
                    You'll be taken to our secure checkout to complete this purchase. Card and Google Pay are supported.
                </p>

                <button
                    onClick={handlePurchase}
                    className="w-full bg-[#c9a227] hover:bg-[#d4ad2e] disabled:opacity-60 text-black font-semibold py-3 rounded-xl transition-colors"
                >
                    {`Pay ${offer.price}`}
                </button>
            </div>
        </ModalWrapper>
    )
}
